import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { Sparkles, ExternalLink, ArrowLeft, Play } from 'lucide-react';
import { applyToolSeo } from '../utils/toolSeo';
import { resolveThumbnailUrl } from '../utils/hlsPlayer';

const API = process.env.REACT_APP_BACKEND_URL;

// /tools/:toolKey — videos + creators using a specific AI tool.
export const ToolDetailPage = () => {
  const { toolKey } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { axiosInstance } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // /made-with/:key is the SEO alias for /tools/:key. We render the same content,
  // but flip a flag to show a richer SEO-friendly intro section.
  const isMadeWith = location.pathname.startsWith('/made-with');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axiosInstance.get(`/tools/${encodeURIComponent(toolKey)}`);
      setData(r.data || null);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, toolKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Inject SEO whenever data loads. Cleanup on unmount restores site defaults.
  useEffect(() => {
    if (!data?.tool) return undefined;
    return applyToolSeo({
      tool: data.tool,
      totalVideos: data.total_videos,
      topVideos: data.top_videos,
      canonicalPath: `/tools/${data.tool.key}`,
    });
  }, [data]);

  const tool = data?.tool;
  const videos = data?.top_videos || [];
  const creators = data?.top_creators || [];

  // Use the shared resolver so Bunny videos (custom_thumbnail_url /
  // thumbnail_url) render alongside legacy MP4s (thumbnail_path). The
  // previous hand-rolled fn only handled thumbnail_path and left Bunny
  // uploads blank.
  const thumbFor = (v) => resolveThumbnailUrl(v);

  return (
    <div className="min-h-screen bg-black pb-24" data-testid="tool-detail-page">
      <div className="px-6 pt-5 pb-3 flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1.5 rounded-full hover:bg-zinc-900">
          <ArrowLeft className="w-5 h-5 text-zinc-400" />
        </button>
        <h1 className="text-sm uppercase tracking-wider text-zinc-500">AI Tool</h1>
      </div>

      {loading && <div className="px-6 py-10 text-center text-zinc-500 text-sm">Loading…</div>}

      {!loading && tool && (
        <>
          <div className="px-6 py-3">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-8 h-8 text-white/80" />
              </div>
              <div className="flex-1 min-w-0">
                <h2 className="text-2xl font-bold text-white" data-testid="tool-name">
                  {isMadeWith ? `Made with ${tool.name}` : tool.name}
                </h2>
                {tool.description && <p className="text-sm text-zinc-400 mt-1">{tool.description}</p>}
                <div className="flex items-center gap-3 mt-2 text-xs text-zinc-500">
                  <span>{data.total_videos} videos on AUDIVUE</span>
                  {tool.url && (
                    <a
                      href={tool.url}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-1 text-zinc-300 hover:text-white"
                      data-testid="tool-external-link"
                    >
                      Visit site <ExternalLink className="w-3 h-3" />
                    </a>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* SEO-friendly intro copy — short, keyword-rich, indexable */}
          <div className="px-6 mt-4">
            <p className="text-sm text-zinc-400 leading-relaxed">
              Stream the best AI music videos made with <span className="text-white font-medium">{tool.name}</span> on AUDIVUE.
              {data.total_videos > 0 && (
                <> Browse <span className="text-white font-medium">{data.total_videos}</span> {tool.name} videos from independent AI artists worldwide.</>
              )}
              {data.total_videos === 0 && (
                <> Be the first to upload an AI music video made with {tool.name}.</>
              )}
            </p>
          </div>

          {creators.length > 0 && (
            <div className="px-6 mt-5">
              <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-2">Top creators using {tool.name}</h3>
              <div className="flex gap-3 overflow-x-auto pb-2 -mx-6 px-6 scrollbar-hide">
                {creators.map((c) => (
                  <button
                    key={c.user_id}
                    onClick={() => navigate(`/creator/${c.user_id}`)}
                    className="flex-shrink-0 w-24 text-center"
                    data-testid={`tool-creator-${c.user_id}`}
                  >
                    <div className="w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-zinc-700 to-zinc-900 flex items-center justify-center text-white font-bold text-lg">
                      {(c.name || '?').charAt(0).toUpperCase()}
                    </div>
                    <p className="text-xs text-white mt-1.5 truncate">{c.name}</p>
                    <p className="text-[10px] text-zinc-500">{c.uploads} {c.uploads === 1 ? 'video' : 'videos'}</p>
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="px-6 mt-6">
            <h3 className="text-xs uppercase tracking-wider text-zinc-500 mb-3">Top videos with {tool.name}</h3>
            {videos.length === 0 && (
              <p className="text-sm text-zinc-500">Nothing tagged with {tool.name} yet.</p>
            )}
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
              {videos.map((v) => (
                <button
                  key={v.id}
                  onClick={() => navigate(`/video/${v.id}`)}
                  className="group text-left"
                  data-testid={`tool-video-${v.id}`}
                >
                  <div className="aspect-[9/16] rounded-xl bg-zinc-900 overflow-hidden relative">
                    {thumbFor(v) ? (
                      <img src={thumbFor(v)} alt={v.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <Play className="w-8 h-8 text-zinc-700" />
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-white mt-1.5 truncate font-medium">{v.title}</p>
                  <p className="text-[10px] text-zinc-500 truncate">{v.artist || v.username}</p>
                </button>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default ToolDetailPage;
